import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useRHFNestedFormContext } from '@openx/components-core/lib/Form/context/RHFNestedFormContext';
import { InfoTile } from '@openx/components-core/lib/InfoTile/InfoTile';
import { dealFormField, type RboConfig, rboAlertKind } from '@openx/types/deal';
import { PackageFields, type PackageFormState } from '@openx/types/package';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRboDisplayedAlert } from '../RboDisplayedAlertContext';

export const RboGroupValidationAlert = ({ errorMsg }: { errorMsg?: string }) => {
  const { t } = useTranslation();
  const rboDisplayed = useRboDisplayedAlert();
  const setDisplayedKind = rboDisplayed?.setDisplayedKind;

  const { parentForm, isNested } = useRHFNestedFormContext();

  const observedTargeting = useWatch<PackageFormState>({
    name: `${PackageFields.TARGETING}`,
  });

  const observableRboConfig: RboConfig | undefined = useWatch({
    control: parentForm?.control,
    name: dealFormField.RBO_CONFIG,
  });

  const observableDealGroup = useWatch({
    control: parentForm?.control,
    name: dealFormField.METADATA_GROUP_DEAL,
  });

  const snapshotRef = useRef<{ targeting?: string; rbo?: string; dealGroup?: string }>({});

  useEffect(() => {
    if (!isNested || !parentForm) {
      return;
    }
    const targetingString = JSON.stringify(observedTargeting ?? null);
    const rboString = JSON.stringify(observableRboConfig ?? null);
    const dealGroupString = JSON.stringify(observableDealGroup ?? null);

    const snapshot = snapshotRef.current;

    if (
      targetingString === snapshot.targeting &&
      rboString === snapshot.rbo &&
      dealGroupString === snapshot.dealGroup
    ) {
      return;
    }

    snapshotRef.current = { targeting: targetingString, rbo: rboString, dealGroup: dealGroupString };
    parentForm.trigger(dealFormField.METADATA);
  }, [observedTargeting, observableRboConfig, observableDealGroup, isNested, parentForm]);

  const fieldState = parentForm?.getFieldState(dealFormField.METADATA);

  const invalid = fieldState?.invalid;
  const error = fieldState?.error;
  const message = String(error?.message ?? errorMsg ?? '');
  const showRboGroupValidationAlert = invalid && message.startsWith('rboGroupValidationAlert:');
  const format = message.split(':')[1];

  useLayoutEffect(() => {
    if (setDisplayedKind && showRboGroupValidationAlert) {
      setDisplayedKind(rboAlertKind.GROUP_VALIDATION);
      return () => setDisplayedKind(prev => (prev === rboAlertKind.GROUP_VALIDATION ? null : prev));
    }
  }, [showRboGroupValidationAlert, setDisplayedKind]);

  if (!showRboGroupValidationAlert) {
    return null;
  }

  return (
    <InfoTile
      data-test="rbo-group-validation-alert"
      severity="error"
      icon={<AutoAwesomeOutlinedIcon />}
      item={{
        title: t('Engagement Optimization'),
        body: (
          <>
            {t('The feature is applied to another deal in this group with a different inventory format of ')}
            <strong>{format}</strong>
            {t('. All grouped deals that use the feature must share the same inventory format.')}
          </>
        ),
      }}
    />
  );
};
